// ─────────────────────────────────────────────────────────────────────────────
// graphSparks — luminous density sampled from REAL edges.
//
// EVERY SPARK BELONGS TO A RELATION THAT EXISTS. A spark is a point sampled on
// the segment between two nodes that actually relate. It is not a graph record:
// no id, no store entry, no label, no pick id, and it can never be selected. The
// standing rule is that every SELECTABLE point represents a real node — sparks
// are not selectable, which is exactly why they are allowed.
//
// This is the difference between density and decoration. A field of random stars
// would be a lie about how much is in the system; a field of edge samples is the
// system's own connective tissue, drawn.
//
// Built off the render loop — on a patch, a level change or a filter change,
// never per frame — and chunked like everything else, at 65,536 points, with the
// next chunk allocated when one fills. There is no cap.
// ─────────────────────────────────────────────────────────────────────────────

import { EdgeBufferChunk, GRAPH_BUFFER_CHUNK_SIZE } from './graphBuffers';
import { SemanticLevel } from './graphLod';
import { hashUnit } from './graphMath';

export interface SparkBufferChunk {
  count: number;
  position: Float32Array; // count * 3
  aColor: Float32Array; // count * 3
  aAlpha: Float32Array; // count
  aSize: Float32Array; // count
  aPulsePhase: Float32Array; // count
}

export interface SparkBuild {
  chunks: SparkBufferChunk[];
  count: number;
  /** Milliseconds spent sampling, reported rather than estimated. */
  buildMs: number;
}

/**
 * World-space spacing between samples, by semantic level. Wider when the whole
 * galaxy is in frame — the sparks are meant to read as a haze at that distance,
 * not as a bead chain — and tighter as the camera descends into structure.
 */
const SPACING_BY_LEVEL: number[] = [22, 16, 11, 8, 8];

/**
 * Samples any ONE edge may contribute, by level.
 *
 * Spacing alone is not enough. A cross-domain relation can span a thousand world
 * units, so 22-unit spacing gave it forty-five samples; across seventy thousand
 * edges that produced 1.08 MILLION sparks against 25k nodes, and additive
 * blending turned the core of the galaxy into a white slab. This is a SAMPLING
 * parameter — how finely one relation is drawn — not a cap on the graph: every
 * edge is still sampled, and no edge is ever dropped.
 */
const MAX_SAMPLES_BY_LEVEL: number[] = [6, 8, 10, 14, 14];

/** Perpendicular scatter, world units peak-to-peak. Thickness, never curvature. */
const OFFSET_SPREAD = 1.8;

/**
 * Spark light contribution, applied on top of the edge alpha they inherit.
 * Count, spacing, size, colour and offsets are all unchanged — the spatial
 * density is useful; it was only ever too loud.
 */
const EDGE_SPARK_ALPHA_SCALE = 0.32;

const MIN_SIZE = 1.3;
const MAX_SIZE = 3.5;

/**
 * Sample every visible edge into points.
 *
 * The edge chunks are the SAME arrays the line layer draws, so a spark can only
 * exist where a line exists: filters, level and hidden endpoints have already
 * been applied upstream and the sparks inherit every one of those decisions for
 * free.
 */
export function buildSparks(
  edgeChunks: EdgeBufferChunk[],
  level: SemanticLevel,
  now: () => number,
): SparkBuild {
  const started = now();
  const spacing = SPACING_BY_LEVEL[level] || SPACING_BY_LEVEL[0];
  const maxSamples = MAX_SAMPLES_BY_LEVEL[level] || MAX_SAMPLES_BY_LEVEL[0];

  // Pass one: count, so each chunk is allocated once at its exact size.
  let total = 0;
  for (let c = 0; c < edgeChunks.length; c++) {
    const chunk = edgeChunks[c];
    for (let i = 0; i < chunk.count; i++) {
      const dx = chunk.position[i * 6 + 3] - chunk.position[i * 6];
      const dy = chunk.position[i * 6 + 4] - chunk.position[i * 6 + 1];
      const dz = chunk.position[i * 6 + 5] - chunk.position[i * 6 + 2];
      const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const samples = Math.min(maxSamples, Math.max(0, Math.floor(length / spacing) - 1));
      total += samples;
    }
  }

  const chunks: SparkBufferChunk[] = [];
  let cursor = 0;
  let chunk: SparkBufferChunk | null = null;
  let index = 0;

  const alloc = (size: number): SparkBufferChunk => ({
    count: size,
    position: new Float32Array(size * 3),
    aColor: new Float32Array(size * 3),
    aAlpha: new Float32Array(size),
    aSize: new Float32Array(size),
    aPulsePhase: new Float32Array(size),
  });

  // Pass two: fill.
  for (let c = 0; c < edgeChunks.length && total > 0; c++) {
    const source = edgeChunks[c];

    for (let i = 0; i < source.count; i++) {
      const ax = source.position[i * 6];
      const ay = source.position[i * 6 + 1];
      const az = source.position[i * 6 + 2];
      const bx = source.position[i * 6 + 3];
      const by = source.position[i * 6 + 4];
      const bz = source.position[i * 6 + 5];

      const dx = bx - ax;
      const dy = by - ay;
      const dz = bz - az;
      const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const samples = Math.min(maxSamples, Math.max(0, Math.floor(length / spacing) - 1));
      if (!samples) continue;

      const edgeId = source.ids[i] || String(c) + ':' + String(i);
      const edgeAlpha = source.alpha[i * 2];

      // One safe perpendicular per edge. Crossing with a near-parallel axis
      // would collapse it, so the reference axis flips for near-vertical edges.
      const nx = dx / (length || 1);
      const ny = dy / (length || 1);
      const nz = dz / (length || 1);
      const refX = Math.abs(ny) > 0.92 ? 1 : 0;
      const refZ = Math.abs(ny) > 0.92 ? 0 : 1;
      let px = ny * refZ - nz * 0;
      let py = nz * refX - nx * refZ;
      let pz = nx * 0 - ny * refX;
      const plen = Math.sqrt(px * px + py * py + pz * pz) || 1;
      px /= plen; py /= plen; pz /= plen;

      for (let j = 0; j < samples; j++) {
        if (!chunk || index >= chunk.count) {
          const size = Math.min(GRAPH_BUFFER_CHUNK_SIZE, total - cursor);
          if (size <= 0) break;
          chunk = alloc(size);
          chunks.push(chunk);
          index = 0;
        }

        const t = (j + 1) / (samples + 1);
        const offset = (hashUnit(edgeId, 'off' + j) - 0.5) * OFFSET_SPREAD;

        chunk.position[index * 3] = ax + dx * t + px * offset;
        chunk.position[index * 3 + 1] = ay + dy * t + py * offset;
        chunk.position[index * 3 + 2] = az + dz * t + pz * offset;

        // Colour interpolates between the two ends, so a cross-domain relation
        // reads as a gradient from one borough's colour into the other's.
        chunk.aColor[index * 3] =
          source.colour[i * 6] + (source.colour[i * 6 + 3] - source.colour[i * 6]) * t;
        chunk.aColor[index * 3 + 1] =
          source.colour[i * 6 + 1] + (source.colour[i * 6 + 4] - source.colour[i * 6 + 1]) * t;
        chunk.aColor[index * 3 + 2] =
          source.colour[i * 6 + 2] + (source.colour[i * 6 + 5] - source.colour[i * 6 + 2]) * t;

        chunk.aAlpha[index] =
          edgeAlpha * (0.65 + hashUnit(edgeId, 'a' + j) * 0.35) * EDGE_SPARK_ALPHA_SCALE;
        chunk.aSize[index] = MIN_SIZE + hashUnit(edgeId, 's' + j) * (MAX_SIZE - MIN_SIZE);
        chunk.aPulsePhase[index] = hashUnit(edgeId, 'p' + j) * Math.PI * 2;

        index++;
        cursor++;
      }
    }
  }

  return { chunks, count: cursor, buildMs: now() - started };
}
